import type { DocumentId, ProjectId } from '@/shared/types/common.types';
import { uploadParts, waitFor } from '@/shared/utils/upload/multipartUpload';
import type { PartTransport, UploadProgress } from '@/shared/utils/upload/multipartUpload';
import type { CreateDocumentInput, DocumentType } from '../types/document.types';
import type { StoredAsset } from '../types/upload.types';
import { UploadRejectedError } from '../utils/uploadErrors';
import { documentService } from './documentService';
import { uploadService } from './uploadService';

export type UploadStage =
  'queued' | 'uploading' | 'completing' | 'creating' | 'verifying' | 'done' | 'failed';

/** What the file becomes: a new document, or the next revision of an existing one. */
export type UploadTarget =
  | { readonly kind: 'document'; readonly document: Omit<CreateDocumentInput, 'assetId'> }
  | {
      readonly kind: 'revision';
      readonly documentId: DocumentId;
      readonly documentType: Exclude<DocumentType, 'Unknown'>;
      /** The document's etag when the revision was chosen; Core refuses it if the document changed since. */
      readonly etag: string;
      readonly revisionCode: string;
      readonly description: string | null;
    };

export interface DocumentUpload {
  readonly projectId: ProjectId;
  readonly file: File;
  /** The document's title, shown while the file uploads. */
  readonly title: string;
  readonly target: UploadTarget;
}

export interface UploadEvents {
  readonly onStage: (stage: UploadStage) => void;
  readonly onProgress: (progress: UploadProgress) => void;
  /** The document now exists, or has the new revision. */
  readonly onSaved: (documentId: DocumentId) => void;
}

export interface PipelineOptions {
  readonly transport: PartTransport;
  /** Pauses before each read of the file's verification status; the length caps the reads. */
  readonly verifyPollMs: readonly number[];
}

export interface UploadResult {
  readonly documentId: DocumentId;
  /** `Verified`, or still `Verifying` when the polls ran out; the document shows which. */
  readonly asset: StoredAsset;
}

export function documentTypeOf(target: UploadTarget): Exclude<DocumentType, 'Unknown'> {
  return target.kind === 'document' ? target.document.documentType : target.documentType;
}

/**
 * Uploads one file and saves it: open a session, send the parts, complete, create the
 * document or add the revision from the asset, then follow verification.
 *
 * Until the upload completes, failing or aborting releases the session so storage drops
 * its parts. Afterwards the document or revision exists; a rejected file fails that
 * revision, which the person sees on the document.
 */
export async function runDocumentUpload(
  upload: DocumentUpload,
  events: UploadEvents,
  signal: AbortSignal,
  options: PipelineOptions
): Promise<UploadResult> {
  const { projectId, file, target } = upload;

  events.onStage('uploading');
  const session = await uploadService.create(
    projectId,
    { fileName: file.name, sizeBytes: file.size, documentType: documentTypeOf(target) },
    crypto.randomUUID()
  );
  try {
    const parts = await uploadParts({
      file,
      plan: session,
      partUrls: uploadService.partUrlProvider(projectId, session.uploadId),
      transport: options.transport,
      signal,
      onProgress: events.onProgress,
    });
    events.onStage('completing');
    await uploadService.complete(projectId, session.uploadId, parts);
  } catch (error) {
    // Nothing refers to the upload yet; a failed release only leaves it for the sweeper.
    uploadService.abort(projectId, session.uploadId).catch(() => undefined);
    throw error;
  }

  events.onStage('creating');
  let documentId: DocumentId;
  if (target.kind === 'document') {
    const document = { ...target.document, assetId: session.assetId };
    documentId = (await documentService.create(projectId, document, crypto.randomUUID())).id;
  } else {
    const revision = {
      assetId: session.assetId,
      revisionCode: target.revisionCode,
      description: target.description,
    };
    await documentService.addRevision(
      projectId,
      target.documentId,
      revision,
      target.etag,
      crypto.randomUUID()
    );
    documentId = target.documentId;
  }
  events.onSaved(documentId);

  events.onStage('verifying');
  let asset: StoredAsset | null = null;
  for (const pause of options.verifyPollMs) {
    await waitFor(pause, signal);
    asset = await uploadService.asset(projectId, session.assetId);
    if (asset.status === 'Rejected') throw new UploadRejectedError(asset.rejectionCode);
    if (asset.status === 'Verified') break;
  }
  return { documentId, asset: asset ?? (await uploadService.asset(projectId, session.assetId)) };
}
